<script setup lang="ts">
import TheAppleSigninButton from '@/components/BaseComponents/TheAppleSigninButton.vue';
import TheGoogleSigninButton from '@/components/BaseComponents/TheGoogleSigninButton.vue';
import InlineLink from '@/components/InlineLink.vue';
import InlineMessage from '@/components/InlineMessage.vue';
import { useAppleLogin } from '@/composables/useAppleLogin';
import { useAuth } from '@/composables/useAuth.js';
import { useGoogleLogin } from '@/composables/useGoogleLogin';
import { usePlatform } from '@/composables/usePlatform';
import { type MessageSchema } from '@/i18n';
import { useAppStore } from '@/stores/appStore';
import { useEnvStore } from '@/stores/envStore';
import type { UserLoginRequest } from '@/types/api';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import Divider from 'primevue/divider';
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import Password from 'primevue/password';
import { useToast } from 'primevue/usetoast';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const appStore = useAppStore();
const { isNative, platform, isIOS } = usePlatform();

const password = ref('');
const rememberMe = ref(false);

const { login, loginWithGoogle, loginWithApple, errors, isFetching } = useAuth();
errors.value = {};

const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });

const userForm: UserLoginRequest = {
  email: '',
  password: '',
  remember: false,
};

function hasError(fieldName: string) {
  return !!errors.value[fieldName] && errors.value[fieldName].length > 0;
}

const platformText = computed(() => {
  if (isNative.value) {
    return `Mobile App (${platform.value})`;
  } else if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
    return 'PWA';
  } else {
    return 'Web';
  }
});

const platformColor = computed(() => {
  if (isNative.value) {
    return 'text-green-600';
  } else if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
    return 'text-blue-600';
  } else {
    return 'text-gray-600';
  }
});

const languagesList = ref<{ label: string; value: 'fr' | 'en' | 'de' | 'it' }[]>([
  { label: 'Français', value: 'fr' },
  { label: 'English', value: 'en' },
  { label: 'Deutsch', value: 'de' },
  { label: 'Italiano', value: 'it' },
]);

const selectedLanguage = ref(languagesList.value.find((language) => language.value === appStore.language) || languagesList.value[0]);

function updateLanguage() {
  const newLanguage = selectedLanguage.value.value;
  appStore.setLanguage(newLanguage);
}

onMounted(() => {
  const browserLanguage = navigator.language || navigator.languages[0];
  // Extract only the first two letters (e.g., 'en-US' -> 'en')
  const languageCode = browserLanguage.split('-')[0];
  console.log('browser language ' + languageCode);
  // Check if your app supports the detected language
  const supportedLanguages = ['en', 'fr', 'de', 'it'];
  if (supportedLanguages.includes(languageCode)) {
    appStore.setLanguage(languageCode as 'en' | 'fr' | 'de' | 'it');
    selectedLanguage.value = languagesList.value.find((language) => language.value === languageCode) || languagesList.value[0];
  }
});

const envStore = useEnvStore();
const toast = useToast();

const { login: googleLogin, isLoggingIn: isGoogleLoggingIn } = useGoogleLogin();
const { login: appleLogin, isLoggingIn: isAppleLoggingIn } = useAppleLogin();

const handleGoogleLogin = async () => {
  try {
    const result = await googleLogin();
    // Pass the result with credential structure similar to vue3-google-login
    await loginWithGoogle({ credential: result.idToken });
  } catch (error) {
    console.error('[LoginView] Google login failed:', error);
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('auth.login.error'),
      life: 5000,
    });
  }
};

const handleAppleLogin = async () => {
  try {
    console.log('[LoginView] Starting Apple login process...');
    const result = await appleLogin();
    console.log('[LoginView] Apple login returned result:', {
      hasIdToken: !!result.idToken,
      hasAuthCode: !!result.authorizationCode,
      hasProfile: !!result.profile,
    });
    // Pass the result with idToken and authorizationCode
    await loginWithApple(result);
    console.log('[LoginView] Apple login completed successfully');
  } catch (error) {
    console.error('[LoginView] Apple login failed - Error object:', error);
    console.error('[LoginView] Apple login failed - Error type:', typeof error);
    console.error('[LoginView] Apple login failed - Error stringified:', JSON.stringify(error, null, 2));

    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: error instanceof Error ? error.message : t('auth.login.error'),
      life: 5000,
    });
  }
};
</script>
<template>
  <main>
    <div v-if="envStore.environment !== 'production'" class="flex items-center gap-2">
      <span class="rounded-full bg-gray-100 px-2 py-1 text-xs" :class="platformColor">
        {{ platformText }}
      </span>
    </div>

    <div class="mb-4 flex justify-end">
      <Dropdown v-model="selectedLanguage" class="w-40" :options="languagesList" option-label="label" @change="updateLanguage">
        <template #value="slotProps">
          {{ slotProps.value.label }}
        </template>
        <template #option="slotProps">
          {{ slotProps.option.label }}
        </template>
      </Dropdown>
    </div>
    <form @submit.prevent="login(userForm, rememberMe, password)">
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <label for="email">{{ t('auth.user.form.label.mail') }}</label>
          <div>
            <InputText
              id="email"
              v-model="userForm.email"
              type="email"
              class="w-full"
              :invalid="hasError('email')"
              :placeholder="t('auth.user.form.placeholder.mail')"
            />
          </div>
          <InlineMessage v-if="hasError('email')" severity="error">
            {{ errors.email[0] }}
          </InlineMessage>
        </div>

        <div class="flex flex-col gap-2">
          <label for="password">{{ t('auth.user.form.label.password') }}</label>
          <div>
            <Password id="password" v-model="password" toggle-mask :feedback="false" :invalid="hasError('password')" class="w-full" />
          </div>
          <InlineMessage v-if="hasError('password')" severity="error">
            {{ errors.password[0] }}
          </InlineMessage>
          <InlineLink destination="/forgot-password" class="text-sm">
            {{ t('auth.login.forgotPassword') }}
          </InlineLink>
        </div>

        <div class="flex flex-row items-center gap-2">
          <Checkbox v-model="rememberMe" input-id="rememberMe" :binary="true" />
          <label for="rememberMe" class="text-sm">{{ t('auth.user.form.label.remember_me') }}</label>
        </div>

        <Button :loading="isFetching" :disabled="isFetching" :label="t('auth.login.login')" class="mt-8 w-full" size="large" type="submit" />
      </div>
      <div class="relative my-4 flex items-center">
        <div class="flex-grow border-t border-gray-300"></div>
        <span class="mx-4 flex-shrink text-gray-600">{{ t('common.or') }}</span>
        <div class="flex-grow border-t border-gray-300"></div>
      </div>
      <div class="flex flex-col items-center gap-4 align-middle">
        <TheGoogleSigninButton type="sign-in" :loading="isGoogleLoggingIn" :disabled="isGoogleLoggingIn || isFetching" @click="handleGoogleLogin" />

        <TheAppleSigninButton
          v-if="isIOS"
          type="sign-in"
          :loading="isAppleLoggingIn"
          :disabled="isAppleLoggingIn || isFetching"
          @click="handleAppleLogin"
        />
      </div>
    </form>
    <Divider />
    <p class="text-sm">
      {{ t('auth.login.noAccountYet') }}
      <InlineLink destination="/register">{{ t('auth.registration.register') }} </InlineLink>
    </p>
  </main>
</template>
